import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { User, Package, LogOut, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Account = () => {
  const { user, token, logout, updateProfile, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
    setFormData({
      name: user?.name || '',
      phone: user?.phone || '',
      address: user?.address || ''
    });
  }, [isAuthenticated, user]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders?email=${user.email}`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const success = await updateProfile(formData);
    if (success) {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-off_white min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-brand_blue border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-off_white min-h-screen py-12" data-testid="account-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-chewy text-5xl sm:text-7xl text-center mb-12">
          <span className="text-brand_blue">MY</span>{' '}
          <span className="text-brand_red">ACCOUNT</span>
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-chewy text-3xl text-brand_blue">Profile</h2>
                <button
                  onClick={() => setEditing(!editing)}
                  className="bg-brand_yellow text-black p-2 rounded-full border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                  data-testid="edit-profile-button"
                >
                  <Edit className="w-5 h-5" strokeWidth={3} />
                </button>
              </div>

              {editing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="font-nunito font-bold block mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-black rounded-lg font-nunito"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="font-nunito font-bold block mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-black rounded-lg font-nunito"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="font-nunito font-bold block mb-2">Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows="3"
                      className="w-full px-4 py-2 border-2 border-black rounded-lg font-nunito"
                      data-testid="input-address"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-brand_blue text-white px-4 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
                      data-testid="save-profile-button"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="flex-1 bg-white text-black px-4 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="font-nunito text-sm text-gray-600 font-bold">Email</p>
                    <p className="font-nunito font-semibold">{user?.email}</p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-gray-600 font-bold">Name</p>
                    <p className="font-nunito font-semibold">{user?.name || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-gray-600 font-bold">Phone</p>
                    <p className="font-nunito font-semibold">{user?.phone || 'Not set'}</p>
                  </div>
                  <div>
                    <p className="font-nunito text-sm text-gray-600 font-bold">Address</p>
                    <p className="font-nunito font-semibold">{user?.address || 'Not set'}</p>
                  </div>
                </div>
              )}

              <button
                onClick={logout}
                className="w-full mt-6 bg-brand_red text-white px-6 py-3 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover transition-all"
                data-testid="logout-button"
              >
                <LogOut className="inline-block w-5 h-5 mr-2" strokeWidth={3} />
                Logout
              </button>
            </div>
          </div>

          {/* Orders List */}
          <div className="lg:col-span-2">
            <div className="bg-white border-4 border-black rounded-xl p-6 shadow-hard">
              <h2 className="font-chewy text-3xl text-brand_blue mb-6">Order History</h2>
              {orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" strokeWidth={3} />
                  <p className="font-nunito text-lg text-gray-600 font-semibold">No orders yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="bg-off_white border-2 border-black rounded-lg p-4"
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-chewy text-xl text-brand_blue">
                            Order #{order.id.slice(0, 8).toUpperCase()}
                          </p>
                          <p className="font-nunito text-sm text-gray-600">
                            {new Date(order.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className="bg-brand_green/20 text-brand_green px-3 py-1 rounded-full font-nunito text-sm font-bold border-2 border-black capitalize">
                          {order.status}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {order.items.map((item, idx) => (
                          <p key={idx} className="font-nunito text-sm">
                            {item.name} x {item.quantity}
                          </p>
                        ))}
                      </div>
                      <p className="font-chewy text-lg text-brand_red mt-2">
                        ${order.total.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;